import { supabase } from '../lib/supabase';
import { cartEvents } from '../utils/cartEventEmitter';

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  
  const isComponent = !!(obj.components || obj.component || obj.componentId || obj.component_id || obj.sellerComponentId || obj.seller_component_id || obj.images_url);
  
  const product = obj.products || obj.product || obj.globalItem?.products || obj.globalItem?.product || (!isComponent ? (obj.globalItem || obj) : null);
  const component = obj.components || obj.component || obj.globalItem?.components || obj.globalItem?.component || (isComponent ? (obj.globalItem || obj) : null);
  
  // CRITICAL FIX: Ensure itemData is the actual item being purchased (component if available, else product)
  const itemData = isComponent ? component : product;

  // Seller info is now directly passed in obj
  const sellerBusinessName = obj.sellerBusinessName || 'Unknown Seller';
  const sellerBusinessSlug = obj.sellerBusinessSlug || null;
  
  // Resolve thumbnail/image correctly
  let resolvedImage = '/assets/images/no_image.png';
  if (isComponent && component?.images_url?.[0]) {
    const rawImage = component.images_url[0];
    resolvedImage = (rawImage.startsWith('http') || rawImage.startsWith('/assets') || rawImage.startsWith('/api')) ? rawImage : supabase.storage.from('component-images').getPublicUrl(rawImage).data.publicUrl;
  } else if (!isComponent && (product?.seller_image_url?.[0] || product?.thumbnail_path)) {
    const isSellerImage = !!product?.seller_image_url?.[0];
    const rawImage = product.seller_image_url?.[0] || product.thumbnail_path;
    const bucketName = isSellerImage ? 'seller-product-images' : 'product-thumbnails';
    resolvedImage = (rawImage.startsWith('http') || rawImage.startsWith('/assets') || rawImage.startsWith('/api')) ? rawImage : supabase.storage.from(bucketName).getPublicUrl(rawImage).data.publicUrl;
  } else if (isComponent && product?.thumbnail_path) {
    const rawImage = product.thumbnail_path;
    resolvedImage = (rawImage.startsWith('http') || rawImage.startsWith('/assets') || rawImage.startsWith('/api')) ? rawImage : supabase.storage.from('product-thumbnails').getPublicUrl(rawImage).data.publicUrl; // Fallback to parent product
  }

  // Determine the primary display name
  const baseName = (isComponent && component?.custom_name && component.custom_name.trim()) || itemData?.name || (isComponent ? 'Unknown Component' : 'Unknown Product');
  
  // Construct descriptive name: "Item Name (for Product Name)"
  let displayName = baseName;
  
  if (obj?.metadata?.type === 'manufacturing') {
    const material = obj.metadata.material || 'Standard';
    const color = obj.metadata.color;
    displayName = `3D Print: ${baseName} (${material}${color ? `: ${color}` : ''})`;
  } else if (isComponent && product?.name && product.name !== baseName) {
    displayName = `${baseName} (for ${product.name})`;
  }

  const finalItem = {
    id: itemData?.id || obj?.id, // Use product/component ID as the primary ID, fallback to cart item ID
    cartItemId: obj?.id, // Keep cart item ID separate
    productId: obj?.product_id,
    componentId: obj?.component_id,
    userId: obj?.user_id,
    sellerId: obj?.seller_id,
    quantity: obj?.quantity || 1,
    createdAt: obj?.created_at,
    selected: true, // Default to selected
    isComponent: isComponent,
    
    name: displayName,
    price: itemData?.price || 0, // This is the current price of the item
    cartPrice: obj?.cart_price || 0, // NEW: Price at the time of addition (renamed from priceAtAddition)
    image: resolvedImage,
    imageAlt: itemData?.name ? `${itemData.name} image` : (isComponent ? 'Component image' : 'Product image'),
    sku: itemData?.sku || itemData?.manufacturer_part_number || null,
    description: itemData?.description || (isComponent ? 'No description available for this component.' : 'No description available for this product.'),
    stockQuantity: itemData?.stock_quantity ?? itemData?.stock_amount ?? 0,
    purchaseLimit: itemData?.purchase_limit || null,
    vinSn: itemData?.vin_sn || null,
    coreCharge: isComponent ? (itemData?.core_charge || 0) : 0,
    customName: isComponent ? component?.custom_name : null, // NEW: customName for components
    
    sellerBusinessName: sellerBusinessName,
    sellerBusinessSlug: sellerBusinessSlug,
    bundleId: obj?.bundle_id, // NEW: Include bundle_id
    metadata: obj?.metadata || {},

    // Keep product and component for detailed debugging/access if needed
    product: product ? {
      id: product?.id,
      name: product?.name,
      price: product?.price,
      sku: product?.sku,
      thumbnailPath: product?.thumbnail_path,
      description: product?.description,
      stockQuantity: product?.stock_quantity,
      categoryId: product?.category_id,
      manufacturerId: product?.manufacturer_id,
      gltfFilePath: product?.gltf_file_path,
      sellerBusinessName: sellerBusinessName,
      sellerBusinessSlug: sellerBusinessSlug,
    } : null,
    component: component ? {
      id: component?.id,
      name: component?.name,
      price: component?.price,
      sku: component?.manufacturer_part_number,
      description: component?.description,
      stockQuantity: component?.stock_quantity,
      thumbnailPath: component?.images_url?.[0],
      sellerBusinessName: sellerBusinessName,
      sellerBusinessSlug: sellerBusinessSlug,
      customName: component?.custom_name // This should be custom_name from the DB for the nested object
    } : null
  };

  return finalItem;
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  return {
    product_id: obj?.componentId ? null : (obj?.sellerProductId || obj?.productId), 
    component_id: obj?.sellerComponentId || obj?.componentId, 
    user_id: obj?.userId,
    seller_id: obj?.sellerId,
    quantity: obj?.quantity,
    cart_price: obj?.cartPrice,
    bundle_id: obj?.bundleId,
    metadata: obj?.metadata || {}
  };
};

export const cartService = {
  async getCartItems(userId) {
    try {
      if (!userId) {
        return [];
      }

      const { data: cartData, error: cartError } = await supabase
        .from('cart_items')
        .select(`
          id, user_id, product_id, component_id, quantity, created_at, cart_price, seller_id, bundle_id, metadata,
          seller_products!cart_items_product_id_fkey(
            id, price, stock_amount, purchase_limit, seller_image_url, product_id, vin_sn,
            products(id, name, sku, thumbnail_path, description, category_id, manufacturer_id, gltf_file_path)
          ),
          seller_components!cart_items_component_id_fkey(
            id, price, stock_quantity, purchase_limit, custom_name, images_url, component_id,
            components(id, name, manufacturer_part_number, description, core_charge)
          ),
          seller_accounts(id, business_name, business_slug)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (cartError) {
        console.error('cartService - Error fetching cart items:', cartError);
        return [];
      }

      if (!cartData || cartData.length === 0) {
        return [];
      }

      const transformedItems = cartData.map(item => {
        const isComponent = !!item.seller_components || !!item.component_id;
        
        let currentItemData = null;
        let parentProductData = null;
        
        let sellerInfo = {
          sellerBusinessName: item.seller_accounts?.business_name || 'OpenGadget',
          sellerBusinessSlug: item.seller_accounts?.business_slug || null
        };
        
        if (item.seller_components) {
           const sc = item.seller_components;
           currentItemData = {
               ...sc.components,
               price: sc.price,
               stock_quantity: sc.stock_quantity || 0,
               purchase_limit: sc.purchase_limit,
               custom_name: sc.custom_name,
               images_url: sc.images_url,
               seller_image_url: sc.images_url
           };
        } else if (item.seller_products) {
           const sp = item.seller_products;
           currentItemData = {
               ...sp.products,
               price: sp.price,
               stock_quantity: sp.stock_amount || 0,
               purchase_limit: sp.purchase_limit,
               seller_image_url: sp.seller_image_url,
               vin_sn: sp.vin_sn
           };
        } else if (item.bundle_id) {
           // Handle legacy or pure bundles if they don't have seller_product_id
           return toCamelCase({
               ...item,
               ...sellerInfo
           });
        } else {
           console.warn(`cartService - Cart item ${item.id} is missing seller offering reference. Migration needed.`);
           return { ...toCamelCase(item), isUnavailable: true, name: 'Product Needs Update (Remove and re-add)' };
        }

        // Pass a combined object to toCamelCase
        return toCamelCase({
          ...item,
          products: !isComponent ? currentItemData : parentProductData,
          components: isComponent ? currentItemData : null,
          ...sellerInfo
        });
      }).filter(item => item !== null);

      return transformedItems;
    } catch (err) {
      console.error('cartService - Unhandled error in getCartItems:', err);
      return [];
    }
  },

  async addToCart(cartItem) {
    try {
      // Determine if it's a product or component and fetch its current price AND purchase limit
      let currentItemPrice = 0;
      let purchaseLimit = null;
      let itemName = 'Item';
      let stockQuantity = 0;
      let vinSn = null;
      let sellerAccountIdForCart = (cartItem.sellerId && String(cartItem.sellerId) !== 'undefined') ? cartItem.sellerId : null;

      if (cartItem?.componentId) {
        // Fetch from seller_components if sellerId is provided, otherwise fallback to component creator
        let targetSellerId = sellerAccountIdForCart;

        let query = supabase
          .from('seller_components')
          .select('id, price, stock_quantity, purchase_limit, custom_name, seller_id')
          .eq('component_id', cartItem.componentId)
          .ilike('status', 'active')
          .or('isdeleted.is.null,isdeleted.eq.false');
          
        if (cartItem.sellerComponentId) {
          query = query.eq('id', cartItem.sellerComponentId);
        } else if (targetSellerId) {
          query = query.eq('seller_id', targetSellerId);
        } else {
          query = query.gt('stock_quantity', 0);
        }

        const { data: sellerComps, error: componentError } = await query
          .order('price', { ascending: true })
          .limit(1);

        const sellerComp = sellerComps?.[0];

        const { data: globalComp } = await supabase.from('components').select('name, custom_name').eq('id', cartItem.componentId).maybeSingle();
        itemName = globalComp?.custom_name || globalComp?.name || 'Component';

        if (componentError || !sellerComp) {
          console.error('Error fetching seller component or not found:', componentError);
          throw new Error('This component is not currently available for purchase from any active seller.');
        } else {
          currentItemPrice = sellerComp?.price || 0;
          purchaseLimit = sellerComp?.purchase_limit;
          stockQuantity = sellerComp?.stock_quantity || 0;
          sellerAccountIdForCart = sellerComp.seller_id;
          cartItem.sellerComponentId = sellerComp.id;
        }
      } else if (cartItem?.productId) {
        let targetSellerId = sellerAccountIdForCart;

        let query = supabase
          .from('seller_products')
          .select('id, price, stock_amount, purchase_limit, seller_id, vin_sn')
          .eq('product_id', cartItem.productId)
          .ilike('status', 'active')
          .or('isdeleted.is.null,isdeleted.eq.false');
          
        if (cartItem.sellerProductId) {
          query = query.eq('id', cartItem.sellerProductId);
        } else if (targetSellerId) {
          query = query.eq('seller_id', targetSellerId);
        } else {
          query = query.gt('stock_amount', 0);
        }

        const { data: sellerProds, error: sellerProdError } = await query
          .order('price', { ascending: true })
          .limit(1);

        const sellerProd = sellerProds?.[0];

        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('name')
          .eq('id', cartItem.productId)
          .single();

        if (productError) throw productError;

        if (!sellerProdError && sellerProd) {
          currentItemPrice = sellerProd.price || 0;
          purchaseLimit = sellerProd.purchase_limit;
          stockQuantity = sellerProd.stock_amount || 0;
          vinSn = sellerProd.vin_sn || null;
          sellerAccountIdForCart = sellerProd.seller_id;
          cartItem.sellerProductId = sellerProd.id;
        } else {
          throw new Error('This product is not currently available for purchase from any active seller.');
        }
        itemName = productData?.name;
      }

      // Determine sellerId (if not already found via seller_components)
      if (!sellerAccountIdForCart) {
        let rawCreatorId = null;

        if (cartItem?.componentId) {
          const { data } = await supabase.from('components').select('created_by').eq('id', cartItem.componentId).single();
          rawCreatorId = data?.created_by;
        } else if (cartItem?.productId) {
          const { data } = await supabase.from('products').select('created_by').eq('id', cartItem.productId).single();
          rawCreatorId = data?.created_by;
        }

        if (rawCreatorId) {
            const { data: sellerAccountData } = await supabase
                .from('seller_accounts')
                .select('id')
                .eq('user_id', rawCreatorId)
                .maybeSingle();
            
            if (sellerAccountData) {
                sellerAccountIdForCart = sellerAccountData.id;
            }
        }
      }

      if (!sellerAccountIdForCart) {
        console.error('❌ cartService.addToCart - Could not determine seller for item');
        throw new Error('This item cannot be added to the cart because it is not associated with a valid store.');
      }
      
      let sellerBusinessName = null;
      let sellerBusinessSlug = null;

      if (sellerAccountIdForCart) {
        const { data: sellerAccountDetails, error: sellerDetailsError } = await supabase
          .from('seller_accounts')
          .select('business_name, business_slug')
          .eq('id', sellerAccountIdForCart)
          .single();

        if (sellerDetailsError) {
          console.warn(`❌ cartService.addToCart - Could not fetch seller details for ID ${sellerAccountIdForCart}:`, sellerDetailsError);
        } else if (sellerAccountDetails) {
          sellerBusinessName = sellerAccountDetails.business_name;
          sellerBusinessSlug = sellerAccountDetails.business_slug;
        }
      }

      const filters = {
        user_id: cartItem?.userId,
        product_id: cartItem?.componentId ? null : (cartItem?.sellerProductId || cartItem?.productId),
        component_id: cartItem?.sellerComponentId || cartItem?.componentId,
        bundle_id: cartItem?.bundleId || null
      };

      let query = supabase
        ?.from('cart_items')
        ?.select('*')
        ?.eq('user_id', filters?.user_id);

      if (filters?.product_id) {
        query = query?.eq('product_id', filters.product_id);
      } else {
        query = query?.is('product_id', null);
      }

      if (filters?.component_id) {
        query = query?.eq('component_id', filters.component_id);
      } else {
        query = query?.is('component_id', null);
      }

      if (filters?.bundle_id) {
        query = query?.eq('bundle_id', filters.bundle_id);
      } else {
        query = query?.is('bundle_id', null);
      }

      if (sellerAccountIdForCart) {
        query = query?.eq('seller_id', sellerAccountIdForCart);
      } else {
        query = query?.is('seller_id', null);
      }

      let existing = null;
      let checkError = null;

      // Do NOT check for existing cart item if it has a VIN (must always be unique row)
      if (!vinSn) {
        const result = await query;
        checkError = result.error;
        if (result.data) {
          existing = result.data.find(item => item.metadata?.type !== 'manufacturing');
        }
      }

      if (checkError) {
        console.error('cartService.addToCart - Check error:', checkError);
        console.trace('Full stack trace for cartService.addToCart Check error');
        throw checkError;
      }

      if (existing && cartItem.metadata?.type !== 'manufacturing') {
        const newQuantity = existing?.quantity + (cartItem?.quantity || 1);

        if (purchaseLimit && newQuantity > purchaseLimit) {
          throw new Error(`You can only purchase a maximum of ${purchaseLimit} units of "${itemName}". You already have ${existing.quantity} in your cart.`);
        }
        
        // stock check omitted per requirements, items can be updated and will be unchecked in cart

        const updatePayload = {
            quantity: newQuantity,
            seller_id: sellerAccountIdForCart
        };

        const { data, error } = await supabase
          ?.from('cart_items')
          ?.update(updatePayload)
          ?.eq('id', existing?.id)
          ?.select(`
            *,
            seller_products!cart_items_product_id_fkey(id, price, stock_amount, purchase_limit, seller_image_url, product_id, products(id, name, sku, thumbnail_path, description, category_id, manufacturer_id, gltf_file_path)),
            seller_components!cart_items_component_id_fkey(id, price, stock_quantity, purchase_limit, custom_name, images_url, component_id, components(id, name, manufacturer_part_number, description, core_charge))
          `)
          ?.single();
        
        if (error) {
          console.error('cartService.addToCart - Update error:', error);
          throw error;
        }
        
        cartEvents.emit();

        return toCamelCase({
          ...data,
          products: data.seller_products ? { 
            ...data.seller_products.products, 
            price: currentItemPrice, 
            stock_quantity: data.seller_products.stock_amount || 0,
            purchase_limit: data.seller_products.purchase_limit,
            seller_image_url: data.seller_products.seller_image_url
          } : null,
          components: data.seller_components ? { 
            ...data.seller_components.components, 
            price: currentItemPrice,
            stock_quantity: data.seller_components.stock_quantity || 0,
            purchase_limit: data.seller_components.purchase_limit,
            custom_name: data.seller_components.custom_name,
            images_url: data.seller_components.images_url,
            seller_image_url: data.seller_components.images_url
          } : null,
          sellerBusinessName: sellerBusinessName,
          sellerBusinessSlug: sellerBusinessSlug
        });
      } else {
        const addedQuantity = cartItem?.quantity || 1;
        
        if (purchaseLimit && addedQuantity > purchaseLimit) {
          throw new Error(`You can only purchase a maximum of ${purchaseLimit} units of "${itemName}".`);
        }
        
        // stock check omitted per requirements, items can be added and will be unchecked in cart

        const insertData = toSnakeCase(cartItem);
        delete insertData.price; // Remove 'price' to prevent schema errors
        
        if (cartItem.metadata?.type === 'manufacturing' && cartItem.metadata?.price != null) {
            insertData.cart_price = Number(cartItem.metadata.price);
        } else if (cartItem.bundleId && cartItem.price != null) {
            insertData.cart_price = Number(cartItem.price);
        } else {
            insertData.cart_price = currentItemPrice;
        }
        
        insertData.seller_id = sellerAccountIdForCart; 
        insertData.bundle_id = cartItem.bundleId || null;

        const { data, error } = await supabase
          ?.from('cart_items')
          ?.insert(insertData)
          ?.select(`
            *,
            seller_products!cart_items_product_id_fkey(id, price, stock_amount, purchase_limit, seller_image_url, product_id, products(id, name, sku, thumbnail_path, description, category_id, manufacturer_id, gltf_file_path)),
            seller_components!cart_items_component_id_fkey(id, price, stock_quantity, purchase_limit, custom_name, images_url, component_id, components(id, name, manufacturer_part_number, description, core_charge))
          `)
          ?.single();
        
        if (error) {
          console.error('cartService.addToCart - Insert error:', error);
          throw error;
        }
        
        cartEvents.emit();

        return toCamelCase({
          ...data,
          products: data.seller_products ? { 
            ...data.seller_products.products, 
            price: currentItemPrice, 
            stock_quantity: data.seller_products.stock_amount || 0,
            purchase_limit: data.seller_products.purchase_limit,
            seller_image_url: data.seller_products.seller_image_url
          } : null,
          components: data.seller_components ? { 
            ...data.seller_components.components, 
            price: currentItemPrice,
            stock_quantity: data.seller_components.stock_quantity || 0,
            purchase_limit: data.seller_components.purchase_limit,
            custom_name: data.seller_components.custom_name,
            images_url: data.seller_components.images_url,
            seller_image_url: data.seller_components.images_url
          } : null,
          sellerBusinessName: sellerBusinessName,
          sellerBusinessSlug: sellerBusinessSlug
        });
      }
    } catch (error) {
      console.error('cartService.addToCart - Error:', error);
      throw error;
    }
  },

  async updateQuantity(cartItemId, quantity) {
    const { data: cartItemDataArr, error: fetchError } = await supabase
      ?.from('cart_items')
      ?.select('seller_id, product_id, component_id, bundle_id')
      ?.eq('id', cartItemId)
      ?.limit(1);
      
    const cartItemData = cartItemDataArr?.[0];

    if (fetchError || !cartItemData) throw fetchError || new Error('Cart item not found');

    let purchaseLimit = null;
    let stockQuantity = null;
    let itemName = 'Item';

    if (cartItemData.component_id) {
      if (cartItemData.seller_id) {
        const { data: sellerCompArr } = await supabase.from('seller_components').select('purchase_limit, stock_quantity, custom_name').eq('component_id', cartItemData.component_id).eq('seller_id', cartItemData.seller_id).limit(1);
        const sellerComp = sellerCompArr?.[0];
        if (sellerComp) {
          purchaseLimit = sellerComp.purchase_limit;
          stockQuantity = sellerComp.stock_quantity;
          itemName = sellerComp.custom_name || 'Component';
        }
      }
      if (!itemName || itemName === 'Component') {
        const { data: globalCompArr } = await supabase.from('components').select('purchase_limit, name, custom_name').eq('id', cartItemData.component_id).limit(1);
        const globalComp = globalCompArr?.[0];
        if (purchaseLimit === null) purchaseLimit = globalComp?.purchase_limit;
        itemName = globalComp?.custom_name || globalComp?.name || 'Component';
      }
    } else if (cartItemData.product_id) {
      if (cartItemData.seller_id) {
        const { data: sellerProdArr } = await supabase.from('seller_products').select('purchase_limit, stock_amount').eq('product_id', cartItemData.product_id).eq('seller_id', cartItemData.seller_id).limit(1);
        const sellerProd = sellerProdArr?.[0];
        if (sellerProd) {
          purchaseLimit = sellerProd.purchase_limit;
          stockQuantity = sellerProd.stock_amount;
        }
      }
      const { data: globalProdArr } = await supabase.from('products').select('purchase_limit, name').eq('id', cartItemData.product_id).limit(1);
      const globalProd = globalProdArr?.[0];
      if (purchaseLimit === null) purchaseLimit = globalProd?.purchase_limit;
      itemName = globalProd?.name || 'Product';
    }
if (purchaseLimit && quantity > purchaseLimit) {
  quantity = purchaseLimit;
}

if (stockQuantity !== null && stockQuantity !== undefined && quantity > stockQuantity) {
  quantity = stockQuantity;
}

// Ensure quantity is at least 1
if (quantity < 1) {
  quantity = 1;
}

    let query = supabase
      ?.from('cart_items')
      ?.update({ quantity }) // removed seller_id update since it was redundant
      .select(`
        *,
        seller_products!cart_items_product_id_fkey(id, price, stock_amount, purchase_limit, seller_image_url, product_id, products(id, name, sku, thumbnail_path, description, category_id, manufacturer_id, gltf_file_path, shipping_weight)),
        seller_components!cart_items_component_id_fkey(id, price, stock_quantity, purchase_limit, custom_name, images_url, component_id, components(id, name, manufacturer_part_number, description, core_charge))
      `);

    if (cartItemData.bundle_id) {
      query = query.eq('bundle_id', cartItemData.bundle_id);
    } else {
      query = query.eq('id', cartItemId);
    }

    const { data, error } = await query;
    
    if (error) throw error;

    // Use the first returned record to pass back to the caller
    const returnedData = data && data.length > 0 ? data[0] : null;

    if (!returnedData) {
      throw new Error('Failed to retrieve updated cart item.');
    }

    cartEvents.emit();

    return toCamelCase({
      ...returnedData,
      products: returnedData.seller_products ? { 
      ...returnedData.seller_products.products, 
      price: returnedData.seller_products.price,
      stock_quantity: returnedData.seller_products.stock_amount || 0,
      purchase_limit: returnedData.seller_products.purchase_limit,
      seller_image_url: returnedData.seller_products.seller_image_url
    } : null,
      components: returnedData.seller_components ? { 
      ...returnedData.seller_components.components, 
      price: returnedData.seller_components.price,
      stock_quantity: returnedData.seller_components.stock_quantity || 0,
      purchase_limit: returnedData.seller_components.purchase_limit,
      custom_name: returnedData.seller_components.custom_name,
      images_url: returnedData.seller_components.images_url,
      seller_image_url: returnedData.seller_components.images_url
    } : null
    });
  },

  async removeFromCart(cartItemId) {
    const { error } = await supabase
      ?.from('cart_items')
      ?.delete()
      ?.eq('id', cartItemId);
    
    if (error) throw error;
    
    cartEvents.emit();
  },

  async clearCart(userId) {
    const { error } = await supabase
      ?.from('cart_items')
      ?.delete()
      ?.eq('user_id', userId);
    
    if (error) throw error;
    
    cartEvents.emit();
  },

  async getCartCount(userId) {
    try {
      const { data, error } = await supabase
        ?.from('cart_items')
        ?.select('quantity')
        ?.eq('user_id', userId);

      if (error) throw error;
      return data?.reduce((sum, item) => sum + (item?.quantity || 0), 0) || 0;
    } catch (error) {
      console.error('Error getting cart count:', error);
      return 0;
    }
  }
};